import type { Group, Lead, Pipeline, Stage } from './types';
import { businessMsBetween, DEFAULT_BUSINESS_HOURS, type BusinessHours, type DayHours } from './businessHours';

/** Default KPI buckets by stage name (a tenant can override them in its settings). */
export const NEW_STAGES = ['חדש', 'ליד חם!!!'];
export const CLOSED_STAGES = ['נסגר'];
export const OUT_STAGES = ['לא רלוונטי', 'ללא מענה'];

export type KpiConfig = { newStageNames: string[]; closedStageNames: string[]; outStageNames: string[] };
export const DEFAULT_KPI: KpiConfig = { newStageNames: NEW_STAGES, closedStageNames: CLOSED_STAGES, outStageNames: OUT_STAGES };

export type Kpis = { total: number; week: number; fresh: number; inCare: number; closed: number };

export function kpis(leads: Lead[], stageName: (id: string) => string, now = Date.now(), cfg: KpiConfig = DEFAULT_KPI): Kpis {
  let total = 0, week = 0, fresh = 0, inCare = 0, closed = 0;
  const weekAgo = now - 7 * 86400_000;
  for (const l of leads) {
    total++;
    if (l.leadDate && Date.parse(l.leadDate) >= weekAgo) week++;
    const s = stageName(l.stageId);
    if (cfg.newStageNames.includes(s)) fresh++;
    else if (cfg.closedStageNames.includes(s)) closed++;
    else if (!cfg.outStageNames.includes(s)) inCare++;
  }
  return { total, week, fresh, inCare, closed };
}

/** The queue: a lead is waiting for first contact while its status is "חדש"; it leaves when the status first changes. */
export const QUEUE_STAGE = 'חדש';

/** The group holding the queue (its tab opens from the queue tiles) and the retry group, found by name. */
export const WAITING_GROUP = 'לידים חדשים';
export const RETRY_GROUP = 'לנסות שוב';

/**
 * The home screen's two rows, both over leads that arrived in the date filter's window.
 * queue:  those still without first contact (status חדש) and their average age in the queue so far.
 * period: how long leads waited in the queue — until their status first changed from חדש, or until now if it hasn't —
 *         and how many now wait for another attempt.
 * Waiting is counted in business hours. `sample` is how many leads the period's average rests on: a lead that left the
 * queue before Piper started recording exits has no known wait and is left out rather than guessed.
 */
export type QueueKpis = {
  queue: { count: number; avgMs: number | null; groupId: string | null };
  period: { avgMs: number | null; sample: number; arrived: number };
  retry: { count: number; groupId: string } | null;
};

export function queueKpis(o: {
  periodLeads: Lead[];                   // arrived within the date filter
  groups: Group[];
  stageName: (id: string) => string;
  exits: Record<string, string>;         // opportunity id → left the queue at
  now?: number;
  hours?: BusinessHours;
  holidays?: Map<string, DayHours>;
}): QueueKpis {
  const now = o.now ?? Date.now();
  const busy = (from: number, to: number) => businessMsBetween(from, to, o.hours ?? DEFAULT_BUSINESS_HOURS, o.holidays);
  const arrival = (l: Lead) => (l.leadDate ? Date.parse(l.leadDate) : NaN);
  const inQueue = (l: Lead) => o.stageName(l.stageId).trim() === QUEUE_STAGE;
  const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

  const queued = o.periodLeads.filter(inQueue);
  const ages = queued.map(arrival).filter((t) => !Number.isNaN(t)).map((t) => busy(t, now));

  const periodWaits: number[] = [];
  for (const l of o.periodLeads) {
    const t = arrival(l);
    if (Number.isNaN(t)) continue;
    if (inQueue(l)) { periodWaits.push(busy(t, now)); continue; }
    const left = o.exits[l.opportunityId] ? Date.parse(o.exits[l.opportunityId]) : NaN;
    if (!Number.isNaN(left) && left >= t) periodWaits.push(busy(t, left));
  }

  const waitingGroup = o.groups.find((g) => g.name === WAITING_GROUP) ?? o.groups.find((g) => g.stageNames.includes(QUEUE_STAGE)) ?? null;
  const retryGroup = o.groups.find((g) => g.name === RETRY_GROUP);
  return {
    queue: { count: queued.length, avgMs: avg(ages), groupId: waitingGroup?.id ?? null },
    period: { avgMs: avg(periodWaits), sample: periodWaits.length, arrived: o.periodLeads.length },
    retry: retryGroup ? { count: o.periodLeads.filter((l) => retryGroup.stageNames.includes(o.stageName(l.stageId))).length, groupId: retryGroup.id } : null,
  };
}

export function stageIndex(pipelines: Pipeline[]): Map<string, Stage & { pipelineId: string }> {
  const m = new Map<string, Stage & { pipelineId: string }>();
  for (const p of pipelines) for (const s of p.stages) m.set(s.id, { ...s, pipelineId: p.id });
  return m;
}

/** Distinct stage names across pipelines, in first-seen position order. */
export function stageVocabulary(pipelines: Pipeline[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of pipelines) for (const s of [...p.stages].sort((a, b) => a.position - b.position)) {
    if (!seen.has(s.name)) { seen.add(s.name); out.push(s.name); }
  }
  return out;
}

/** Find the stage id with the given name inside a specific pipeline. */
export function stageIdByName(pipeline: Pipeline, name: string): string | null {
  return pipeline.stages.find((s) => s.name === name)?.id ?? null;
}
